import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { switchLocale } from '../i18n';
import {
  LOCALES,
  localeFromPath,
  localizedPath,
  nativeLanguageName,
  pathWithoutLocale,
  rememberLocale,
} from '../i18n/config';
import { CheckIcon, ChevronDownIcon, GlobeIcon } from './Icons';

/**
 * Language picker: a button with a listbox (WAI-ARIA "select-only combobox" pattern).
 * Choosing a language opens the same page under that locale's URL prefix; pages without one
 * (the dashboard) switch language in place. Either way the choice is remembered.
 */
export function LanguageMenu() {
  const { t, i18n } = useTranslation();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const current = localeFromPath(pathname) ?? i18n.language;
  const currentIndex = Math.max(0, LOCALES.indexOf(current));

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(currentIndex);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();
  const optionId = (locale: string) => `${listId}-${locale}`;

  useEffect(() => {
    if (!open) return;
    listRef.current?.focus();
    const closeOnOutside = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || !rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', closeOnOutside);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside);
    };
  }, [open]);

  const openMenu = () => {
    setActive(currentIndex);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  const choose = (locale: string) => {
    close();
    if (locale === current) return;
    rememberLocale(locale);
    if (localeFromPath(pathname) === undefined) {
      void switchLocale(i18n, locale); // the dashboard: same page, other language
    } else {
      void navigate(localizedPath(locale, pathWithoutLocale(pathname)) + search + hash);
    }
  };

  const onListKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const last = LOCALES.length - 1;
    const moves: Record<string, () => number> = {
      ArrowDown: () => (active === last ? 0 : active + 1),
      ArrowUp: () => (active === 0 ? last : active - 1),
      Home: () => 0,
      End: () => last,
    };
    const move = moves[event.key];
    if (move !== undefined) {
      event.preventDefault();
      setActive(move());
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choose(LOCALES[active] ?? current);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className="lang-menu" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="lang-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${t('nav.language')}: ${nativeLanguageName(current)}`}
        onClick={() => {
          if (open) close();
          else openMenu();
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            openMenu();
          }
        }}
      >
        <GlobeIcon />
        <span className="lang-name" lang={current}>
          {nativeLanguageName(current)}
        </span>
        <span className="lang-code" aria-hidden="true">
          {current.toUpperCase()}
        </span>
        <ChevronDownIcon />
      </button>
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          className="lang-list"
          aria-label={t('nav.language')}
          aria-activedescendant={optionId(LOCALES[active] ?? current)}
          onKeyDown={onListKeyDown}
        >
          {LOCALES.map((locale, index) => (
            <li
              key={locale}
              id={optionId(locale)}
              role="option"
              lang={locale}
              aria-selected={locale === current}
              className={index === active ? 'is-active' : undefined}
              onPointerMove={() => {
                setActive(index);
              }}
              onClick={() => {
                choose(locale);
              }}
            >
              <span>{nativeLanguageName(locale)}</span>
              {locale === current && <CheckIcon />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
